'use client';
import { useMedia } from '@ssv-website/hooks';
import { Logo } from '../logo';
import { CosImage, getCosImageUrl } from '../cos-image';
import public_account from './images/public-account.png';
import video_code from './images/video-number.png';
import follow_wechat from './images/follow-wechat.svg';
import follow_video from './images/follow-video.svg';
import logo_icon from './images/footer-logo.png';
import footerList, { getColor, Theme } from './preset';
import SSVLink from '../ssv-link';

const ASSET_PATH = {
  bgWhitePc: 'web-ui/footer/images/footer-bg.svg',
  bgWhiteMobile: 'web-ui/footer/images/footer-mobile-white-bg.svg',
  bgBluePc: 'web-ui/footer/images/footer-blue-bg.png',
  bgBlueMobile: 'web-ui/footer/images/footer-blue-mobile-bg.svg',
  logo: 'web-ui/footer/images/footer-logo.png',
  videoCode: 'web-ui/footer/images/video-number.png',
  publicAccount: 'web-ui/footer/images/public-account.png',
} as const;

export const Footer = (props: { theme?: Theme; className?: string }) => {
  const { theme = 'white', className = '' } = props;
  const isMobile = useMedia('mobile');
  const color = getColor(theme);
  const bgUrl =
    theme === 'blue'
      ? getCosImageUrl(isMobile ? ASSET_PATH.bgBlueMobile : ASSET_PATH.bgBluePc)
      : getCosImageUrl(isMobile ? ASSET_PATH.bgWhiteMobile : ASSET_PATH.bgWhitePc);
  return (
    <div
      id="Footer"
      className={`w-full flex justify-center bg-no-repeat bg-cover ${className}`}
      style={{
        backgroundImage: bgUrl ? `url(${bgUrl})` : undefined,
        backgroundPosition: !isMobile ? '60% 0' : '0 30%',
        color: color ?? '#353f49',
        borderTop: theme === 'white' ? '1px solid #dcdcdc' : undefined,
      }}
    >
      <div className="max-w-5xl flex flex-col w-full desktop:px-5">
        <div className="desktop:pr-5">
          <Logo
            className="w-[160px] h-[38px] mobile:hidden mt-9"
            src={theme === 'blue' ? logo_icon : undefined}
            path={theme === 'blue' ? ASSET_PATH.logo : undefined}
          />

        </div>
        <div className="flex justify-between mt-20 mobile:flex-col-reverse mobile:mt-16 mobile:gap-9">
          <FooterList isMobile={isMobile} />
          <div className="hidden mobile:block">
            <DimensionalCode />
          </div>
        </div>
        <FootNote />
      </div>
    </div>
  );
};

const FooterList = (props: { isMobile: boolean }) => {
  const { isMobile } = props;
  const list = footerList;

  return (
    <div className="flex flex-1 mobile:justify-evenly">
      {list.map((item, index) => (
        <div key={index} className={isMobile ? '' : 'w-[200px] flex-none'}>
          <div className={`font-600 text-base mb-5 mobile:text-sm mobile:mb-3`}>
            {item.title}
          </div>
          <div className={isMobile ? '' : 'flex flex-col gap-[10px]'}>
            {item.list.map((v, i) => (
              <SSVLink url={v.path} isBlank={v.isBlank} key={v.text + i}>
                <div
                  className={`font-400 opacity-80 cursor-pointer hover:opacity-100 hover:font-500 ${
                    isMobile
                      ? 'text-sm leading-none mb-3'
                      : 'text-base leading-[22px]'
                  }`}
                >
                  {v.text}
                </div>
              </SSVLink>
            ))}
          </div>
        </div>
      ))}
      <FollowUs />
    </div>
  );
};

const FollowUs = () => (
  <div className="w-[200px] flex-none mobile:hidden">
    <div className="font-600 text-base mb-5">关注我们</div>
    <div className="flex flex-col gap-[10px]">
      <FollowCode
        code={video_code}
        path={ASSET_PATH.videoCode}
        icon={follow_video}
      />
      <FollowCode
        code={public_account}
        path={ASSET_PATH.publicAccount}
        icon={follow_wechat}
      />
    </div>
  </div>
);

const FollowCode = (props: {
  code: typeof video_code;
  path: string;
  icon: typeof follow_video;
}) => {
  const { code, path, icon } = props;
  return (
    <div className="group relative h-[22px] w-[22px] cursor-pointer">
      <img
        src={icon.src}
        alt=""
        className="h-[22px] w-[22px] opacity-80 transition-opacity group-hover:opacity-100"
      />
      <div className="absolute bottom-[28px] left-0 z-50 hidden h-[148px] w-[148px] rounded-tl-xl rounded-tr-xl rounded-br-xl bg-white p-[10px] group-hover:block">
        <CosImage
          fallbackSrc={code}
          path={path}
          alt=""
          className="h-[129px] w-[129px] object-cover"
        />
      </div>
    </div>
  );
};

const DimensionalCode = () => {
  return (
    <div className="flex gap-2 justify-center">
      <div className="font-400">
        <CosImage
          fallbackSrc={video_code}
          path={ASSET_PATH.videoCode}
          alt=""
          className={`object-cover w-32 h-32`}
        />
        <div className={`text-center opacity-40 mt-1 text-sm`}>
          关注官方视频号
        </div>
      </div>
      <div className="font-400">
        <CosImage
          fallbackSrc={public_account}
          path={ASSET_PATH.publicAccount}
          alt=""
          className={`object-cover w-32 h-32`}
        />
        <div className={`text-center opacity-40 mt-1 text-sm`}>
          关注官方公众号
        </div>
      </div>
    </div>
  );
};
const FootNote = () => {
  return (
    <div
      className={`opacity-40 w-full text-base mobile:text-xss mb-5 mt-16 mobile:mt-12 mobile:mb-5 mobile:text-center`}
    >
      Copyright © 1998 - 2025 Tencent. All Rights Reserved.
    </div>
  );
};
